/* eslint-disable @typescript-eslint/no-use-before-define */
import React from 'react';
import styled from 'styled-components';
import axios from 'axios';
import { VerticalSpace, Button, message } from '@brokerbay/design-components';
import { Modal, Text, breakpoints, Title, Popconfirm, Alert } from 'components';
import {
  AuthContext,
  OnboardingPreLaunchModalContext,
  OnboardingWelcomeModalContext,
} from 'contexts';
import {
  formatDateStrWithYear,
  formatDateStrWithYearShort,
  formatTimeStr,
} from 'utils';
import errorReporter from '@app/errorReporter';
import { shiftToUtc } from '../../Router/onboarding/util';
import { WATCH_HOW_IT_WORKS_LINK } from './constants';
import LaunchSteps from './LaunchSteps';
import VideoModal from './VideoModal';

export const OnboardingPreLaunchModal: React.FC = () => {
  const [videoModalVisible, setVideoModalVisible] = React.useState(false);
  const [isWorking, setIsWorking] = React.useState(false);

  const {
    onboardingPortalConfig,
    onboarding,
    fetchOnboarding,
    isOrgPlanUpgraded,
  } = React.useContext(AuthContext);
  const { closeModal, visible } = React.useContext(
    OnboardingPreLaunchModalContext,
  );
  const { setIsDoneWelcomeFlow } = React.useContext(
    OnboardingWelcomeModalContext,
  );

  const { auth } = React.useContext(AuthContext);
  const isGlobalAdminOrAbove = auth.roles[auth.role] > auth.roles.BRANCH_ADMIN;
  const isAdmin = auth.roles[auth.role] >= auth.roles.TEAM_ADMIN;
  const isAgent = auth.roles[auth.role] === auth.roles.AGENT;

  const handleCloseModal = () => {
    closeModal();
    setIsDoneWelcomeFlow(true);
  };

  const handleViewVideo = () => {
    setVideoModalVisible(true);
  };

  const handleResendPrelaunchEmail = async () => {
    try {
      setIsWorking(true);
      await axios.post('/onboarding/emails/send_agent_launch');
      message.success('Agent launch emails sent!');
      await fetchOnboarding();
    } catch (err) {
      errorReporter.error(err);
      message.error('Unable to send launch emails');
    } finally {
      setIsWorking(false);
    }
  };
  const emailConfirmTextPreamble = onboarding?.emailSent
    ? `Someone at your brokerage sent this info to your agents on ${formatDateStrWithYear(
        onboarding.emailSent,
      )} at ${formatTimeStr(
        onboarding.emailSent,
      )}. If you press Continue, this will send another email with all launch and registration info to every agent at your brokerage.`
    : 'This will send an email with all launch and registration info to every agent at your brokerage.';

  const emailConfirmText = `${emailConfirmTextPreamble} Please note BrokerBay will send a reminder to all agents at your brokerage three days before launch, and again on the morning of launch.`;

  return (
    <>
      <VideoModal
        url={WATCH_HOW_IT_WORKS_LINK}
        open={videoModalVisible}
        handleClose={() => {
          setVideoModalVisible(false);
        }}
        title="How it works!"
        data-cy="org-onboarding-how-it-works-video-modal"
        destroyOnClose
      />
      <Modal
        open={visible}
        title="Getting Ready for Launch"
        footer={null}
        width={breakpoints.sm}
        onCancel={handleCloseModal}
        className="org-onboarding-getting-ready-for-launch-modal"
      >
        <VerticalSpace size={8}>
          {(isGlobalAdminOrAbove ||
            auth.roles[auth.role] === auth.roles.BRANCH_ADMIN ||
            auth.isBrokerOfRecord) &&
            onboarding &&
            isOrgPlanUpgraded && (
              <StyledAlert
                message={`Your subscription is confirmed! Your first bill will start ${formatDateStrWithYearShort(
                  shiftToUtc(onboarding.launchDate),
                )}`}
                type="success"
                showIcon
                data-cy="subscription-confirmed-alert"
              />
            )}

          <TitleContainer>
            <Title>Next Steps</Title>
            {isGlobalAdminOrAbove && (
              <Popconfirm
                title={emailConfirmText}
                okText="Continue"
                cancelText="Cancel"
                onConfirm={handleResendPrelaunchEmail}
                maxWidth={650}
                placement="bottom"
              >
                <Button data-cy="email-agents-button" disabled={isWorking}>
                  Email info to agents
                </Button>
              </Popconfirm>
            )}
          </TitleContainer>
          <Text type="secondary">
            {isOrgPlanUpgraded
              ? 'Your brokerage has upgraded to BrokerBay’s full brokerage ecosystem! ' +
                'Make sure to register for training below and set up your listings with showing instructions.'
              : 'It’s time to get your listings setup and ensure everyone at your office is trained!'}
          </Text>
          {/* TODO: APP-4507 - Reintroduce this image/link when a video is recorded */}
          {/* <StyledImg
            onClick={handleViewVideo}
            src={IMAGE_PLACEHOLDER}
            data-cy="org-onboarding-next-steps-image"
          /> */}
          <LaunchSteps
            portalConfig={onboardingPortalConfig}
            launchDate={onboarding?.launchDate}
            watchHowItWorksLink={WATCH_HOW_IT_WORKS_LINK}
            onVideoLinkClicked={handleViewVideo}
            isAdmin={isAdmin}
            isAgent={isAgent}
            isOrgPlanUpgraded={isOrgPlanUpgraded}
          />
        </VerticalSpace>
      </Modal>
    </>
  );
};

const StyledImg = styled.img`
  width: 100%;
  cursor: pointer;
`;

const StyledAlert = styled(Alert)`
  margin-bottom: 20px;
`;

const TitleContainer = styled.div`
  display: flex;
  flex-flow: row nowrap;
  justify-content: space-between;
`;
